import { reactive } from 'vue';
import { COPY, defaultCopy, interpolate, type CopyKey } from '@jcao/contracts';
import { runtimeConfig } from './runtime.ts';

/**
 * Interface copy, fetched once at boot.
 *
 * The shipped wording is compiled into the bundle, so `t()` works before the
 * fetch finishes, and keeps working if it fails. A copy service that is down
 * must not produce a page of blanks — it produces the wording the image was
 * built with, which is always a complete, correct English page.
 */

const state = reactive<{ strings: Record<string, string>; loaded: boolean; version: string }>({
  strings: {},
  loaded: false,
  version: '',
});

const BASE = () => runtimeConfig().apiBaseUrl;
const CACHE_KEY = 'jcao.copy';

export async function loadCopy(): Promise<void> {
  // Last known copy shows immediately on a repeat visit; the fetch below
  // replaces it. Failing to read the cache is not an error worth surfacing.
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached) as { strings: Record<string, string>; version: string };
      state.strings = parsed.strings;
      state.version = parsed.version;
    }
  } catch { /* no cache, or storage unavailable */ }

  try {
    const res = await fetch(`${BASE()}/v1/copy`, {
      headers: state.version ? { 'If-None-Match': `"${state.version}"` } : {},
    });
    if (res.status === 304) { state.loaded = true; return; }
    if (!res.ok) throw new Error(String(res.status));
    const body = (await res.json()) as { strings: Record<string, string>; version: string };
    state.strings = body.strings;
    state.version = body.version;
    try { localStorage.setItem(CACHE_KEY, JSON.stringify(body)); } catch { /* private mode */ }
  } catch {
    // Silent on purpose: the defaults are already correct English, and an
    // error banner about copy would itself be noise.
  } finally {
    state.loaded = true;
  }
}

/**
 * `t('home.title')`. The key is typed against the catalogue, so a typo is a
 * compile error rather than an empty string at runtime.
 */
export function t(key: CopyKey, vars: Record<string, string | number> = {}): string {
  const override = state.strings[key];
  const text = override && override.trim() ? override : defaultCopy(key);
  return interpolate(text, vars);
}

/** For a component that needs to react to copy loading. */
export function useCopy() {
  return { t, state };
}

/** Exposed so a test can assert the catalogue and the app agree. */
export { COPY };
